import React from 'react';
import { Alert, Box, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatLabel(value, fallback = 'N/A') {
  const label = String(value || '').trim();
  if (!label) return fallback;

  return label
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatStatus(value) {
  return formatLabel(value, 'Pending');
}

function getStatusColor(status) {
  const normalized = String(status || '').toLowerCase();
  if (['approved', 'completed', 'active'].includes(normalized)) return 'success';
  if (['rejected', 'blocked'].includes(normalized)) return 'error';
  if (normalized === 'pending') return 'warning';
  return 'default';
}

function getVersion(workflow, instance) {
  return workflow?.published_version || workflow?.publishedVersion || instance?.version || null;
}

function pathFromTasks(tasks) {
  return asArray(tasks).reduce((path, task) => {
    const taskPath = asArray(task?.metadata?.path);
    taskPath.forEach((step) => {
      const last = path[path.length - 1];
      if (!last || last.node_id !== step.node_id) {
        path.push(step);
      }
    });
    return path;
  }, []);
}

function pathFromEvents(events) {
  return asArray(events).reduce((path, event) => {
    const eventPath = asArray(event?.payload?.path);
    eventPath.forEach((step) => {
      const last = path[path.length - 1];
      if (!last || last.node_id !== step.node_id) {
        path.push(step);
      }
    });
    return path;
  }, []);
}

// Derive a linear path from the published graph itself by walking the
// `default` edges starting at the start node. This guarantees the form
// always shows the static workflow path even when no live simulation
// result is available (avoids a stale render after the simulate API was
// deferred to fix a render loop on free-text input).
function pathFromGraph(graph) {
  const nodes = {};
  for (const node of asArray(graph?.nodes)) {
    if (node && node.id != null) nodes[String(node.id)] = node;
  }
  if (!Object.keys(nodes).length) return [];

  const edgesBySource = {};
  for (const edge of asArray(graph?.edges)) {
    if (!edge) continue;
    const source = String(edge.source ?? '');
    if (!source) continue;
    (edgesBySource[source] = edgesBySource[source] || []).push(edge);
  }

  let startId = null;
  for (const id of Object.keys(nodes)) {
    if ((nodes[id].type || '') === 'start') { startId = id; break; }
  }
  if (!startId) return [];

  const path = [];
  const seen = new Set();
  let current = startId;
  let guard = Object.keys(nodes).length + 5;

  while (current && !seen.has(current) && guard-- > 0) {
    seen.add(current);
    const node = nodes[current];
    if (!node) break;
    const label = node?.data?.label || node.id;
    path.push({
      node_id: node.id,
      node_type: node.type,
      type: node.type,
      label,
      node_label: label,
      status: 'pending',
      branch: 'default',
    });
    let next = null;
    for (const edge of edgesBySource[current] || []) {
      const branch = String(edge.branch ?? edge?.data?.branch ?? 'default').toLowerCase();
      if (branch === 'default' || branch === '') {
        next = String(edge.target ?? '');
        if (next) break;
      }
    }
    current = next;
  }
  return path;
}

function getDisplayPath(simulation, tasks, events, workflow, instance) {
  const simulationPath = asArray(simulation?.path);
  if (simulationPath.length) return simulationPath;

  const eventPath = pathFromEvents(events);
  if (eventPath.length) return eventPath;

  const taskPath = pathFromTasks(tasks);
  if (taskPath.length) return taskPath;

  // Final fallback: walk the published graph directly so a fresh form
  // (no simulation, no tasks, no events) still shows the approval path.
  const version = getVersion(workflow, instance);
  return pathFromGraph(version?.graph);
}

function taskAssignee(task) {
  return (
    task?.assigned_user?.name ||
    task?.assignedUser?.name ||
    task?.assigned_user_name ||
    task?.approver_employee_code ||
    formatLabel(task?.approver_role, '')
  );
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function eventLabel(event) {
  const base = formatLabel(event?.event_type, 'Workflow Event');
  const nodeLabel = event?.payload?.node_label;
  return nodeLabel ? `${base}: ${nodeLabel}` : base;
}

export default function WorkflowPathPreview({
  title = 'Workflow Path',
  subheader = 'Conditions are evaluated from the current request values.',
  workflow = null,
  simulation = null,
  instance = null,
  tasks = [],
  events = [],
  loading = false,
  error = null,
  compact = false,
}) {
  const theme = useTheme();
  const version = getVersion(workflow, instance);
  const displayPath = getDisplayPath(simulation, tasks, events, workflow, instance);
  const taskRows = asArray(tasks);
  const eventRows = asArray(events).slice(compact ? -4 : -6).reverse();
  const hasEngineData = Boolean(version || instance || displayPath.length || taskRows.length || eventRows.length);

  if (!hasEngineData && !loading && !error) {
    return null;
  }

  return (
    <Box
      sx={{
        p: compact ? { xs: 1.35, sm: 1.5 } : { xs: 1.5, sm: 2 },
        minWidth: 0,
        borderRadius: compact ? 2.5 : 2,
        border: '1px solid',
        borderColor: compact ? alpha(theme.palette.divider, 0.92) : alpha(theme.palette.primary.main, 0.16),
        backgroundColor: compact
          ? alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.78 : 0.94)
          : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.035),
        boxShadow: compact ? theme.shadows[2] : 'none',
        backdropFilter: compact ? 'blur(12px)' : 'none',
        maxHeight: compact ? { md: 'calc(100vh - 320px)' } : 'none',
        overflowY: compact ? { xs: 'visible', md: 'auto' } : 'visible',
        scrollbarGutter: compact ? 'stable' : 'auto',
      }}
    >
      <Stack spacing={compact ? 1.25 : 1.5}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant={compact ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
              {title}
            </Typography>
            {subheader ? (
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {subheader}
              </Typography>
            ) : null}
          </Box>
          {loading ? (
            <CircularProgress size={20} />
          ) : version ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`v${version.version_number || version.id || 'published'}`}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Stack>

        {error ? (
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
            {error}
          </Alert>
        ) : null}

        {instance ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Instance ${formatStatus(instance.status)}`} color={getStatusColor(instance.status)} />
            {instance.current_node_key ? (
              <Chip size="small" variant="outlined" label={`Current node: ${instance.current_node_key}`} />
            ) : null}
          </Stack>
        ) : null}

        {displayPath.length ? (
          <Stack spacing={compact ? 0.85 : 1}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <RouteRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Evaluated Path
              </Typography>
            </Stack>
            {displayPath.map((step, index) => {
              const type = String(step.node_type || '').toLowerCase();
              const isCondition = type === 'condition';
              const decision = step.decision ? String(step.decision).toUpperCase() : '';

              return (
                <Box
                  key={`${step.node_id || step.label || index}-${index}`}
                  sx={{
                    p: compact ? 1 : 1.15,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: isCondition
                      ? alpha(theme.palette.warning.main, 0.34)
                      : alpha(theme.palette.divider, 0.9),
                    backgroundColor: isCondition
                      ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.12 : 0.08)
                      : alpha(theme.palette.background.paper, 0.7),
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Chip size="small" label={index + 1} color={isCondition ? 'warning' : 'primary'} sx={{ fontWeight: 800 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                        {step.label || formatLabel(type, 'Workflow Step')}
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={formatLabel(type, 'Step')} />
                        {isCondition ? (
                          <Chip
                            size="small"
                            color={step.decision === 'true' ? 'success' : 'warning'}
                            icon={<RuleRoundedIcon />}
                            label={`${formatLabel(step.condition_logic || 'and')} -> ${decision || 'DEFAULT'}`}
                          />
                        ) : null}
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : loading ? (
          <Typography variant="body2" color="text.secondary">
            Evaluating workflow path...
          </Typography>
        ) : null}

        {taskRows.length ? (
          <>
            <Divider />
            <Stack spacing={compact ? 0.85 : 1}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <TaskAltRoundedIcon sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Approval Tasks
                </Typography>
              </Stack>
              {taskRows.map((task) => {
                const assignee = taskAssignee(task);
                return (
                  <Box
                    key={task.id || task.task_id || `${task.node_key}-${task.status}`}
                    sx={{
                      p: 1.1,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: alpha(theme.palette.divider, 0.9),
                      backgroundColor: alpha(theme.palette.background.paper, 0.72),
                    }}
                  >
                    <Stack spacing={0.6}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800, minWidth: 0, overflowWrap: 'anywhere' }}>
                          {task.node_label || task.nodeLabel || formatLabel(task.node_key, 'Approval')}
                        </Typography>
                        <Chip size="small" color={getStatusColor(task.status)} label={formatStatus(task.status)} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                        {assignee ? `Assigned to ${assignee}` : 'Assignee is resolved by workflow rule.'}
                        {task.action_at ? ` | Action: ${formatDateTime(task.action_at)}` : ''}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </>
        ) : null}

        {eventRows.length ? (
          <>
            <Divider />
            <Stack spacing={0.8}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {compact ? 'Recent Workflow History' : 'Workflow History'}
              </Typography>
              {eventRows.map((event) => (
                <Box key={event.id || `${event.event_type}-${event.created_at}`} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                    {eventLabel(event)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(event.created_at)}
                    {event.actor?.name ? ` | ${event.actor.name}` : ''}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}
